import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PublicHeader, PublicFooter } from "@/components/layouts/PublicLayout";
import { ArrowRight, BookOpen, Briefcase, Shield, Users } from "lucide-react";

const FOUNDER_IMAGE = "https://customer-assets.emergentagent.com/job_ai-means-biz/artifacts/vlhuza45_R_Dale_Brunet_Image_PNG-removebg-preview.png";

const AboutFounderPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-navy-950 to-navy-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Image Column */}
            <div className="lg:col-span-4">
              <div className="sticky top-28">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-b from-primary/20 to-transparent rounded-2xl blur-3xl" />
                  <img
                    src={FOUNDER_IMAGE}
                    alt="R. Dale Brunet"
                    className="relative w-full max-w-sm mx-auto rounded-2xl"
                    data-testid="founder-image"
                  />
                </div>
                <div className="mt-6 text-center lg:text-left">
                  <h2 className="font-heading text-2xl font-bold text-white">R. Dale Brunet</h2>
                  <p className="text-primary font-medium">Principal Advisor & Founder</p>
                  <p className="text-slate-400 text-sm mt-2">A.I. Means Business</p>
                </div>
              </div>
            </div>

            {/* Content Column */}
            <div className="lg:col-span-8 space-y-12">
              {/* Introduction */}
              <div data-testid="founder-intro">
                <p className="text-primary font-medium mb-4 tracking-wide uppercase text-sm">
                  About Our Founder
                </p>
                <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white mb-6 tracking-tight leading-tight">
                  Guiding Business Leaders Through Technological Disruption—with Clarity, Not Hype
                </h1>
                <p className="text-lg text-slate-300 leading-relaxed">
                  R. Dale Brunet is a seasoned technologist, entrepreneur, and author whose career spans more than four decades at the intersection of <span className="text-white font-medium">technology, business operations, and real-world execution</span>. He is the founder and principal advisor of A.I. Means Business, an advisory practice dedicated to helping established small and medium-sized businesses navigate the disruptive impact of artificial intelligence with confidence and clarity.
                </p>
                <p className="text-lg text-slate-300 leading-relaxed mt-4">
                  Unlike many voices in the A.I. space, Brunet does not approach technology from theory, trend-chasing, or vendor salesmanship. His perspective is grounded in <span className="text-white font-medium">lived experience</span>—as a business owner who has personally carried payroll risk, as a technologist who has worked in mission-critical environments, and as an author who has spent years examining how emerging technologies reshape business, labor, and decision-making.
                </p>
              </div>

              {/* Professional Background */}
              <div className="glass p-8 rounded-2xl" data-testid="founder-background">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-white">
                    A Career Built on Both Sides of the Table
                  </h3>
                </div>
                <p className="text-slate-300 leading-relaxed mb-6">
                  Brunet's professional background is defined by a rare combination: <span className="text-white font-medium">deep technical fluency and practical business accountability</span>.
                </p>
                
                <h4 className="font-heading text-lg font-semibold text-white mb-3">
                  Technology & Enterprise Experience
                </h4>
                <p className="text-slate-300 leading-relaxed mb-4">
                  R. Dale Brunet has worked in complex, high-reliability technology environments, including roles supporting and contributing to large-scale systems for Fortune 500 companies and defense-grade operations. His experience includes work associated with organizations such as:
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                  {["Honeywell", "Unisys", "L-3 Communications", "United States Air Force Satellite Control Network"].map((org, index) => (
                    <li key={index} className="flex items-center gap-2 text-slate-300">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      {org}
                    </li>
                  ))}
                </ul>
                <p className="text-slate-400 text-sm italic">
                  These environments demand precision, discipline, auditability, and an intolerance for ambiguity—principles that continue to shape how he approaches A.I. today.
                </p>
              </div>

              {/* Entrepreneurial Reality */}
              <div data-testid="founder-entrepreneurial">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-white">
                    Entrepreneurial Reality, Not Theory
                  </h3>
                </div>
                <p className="text-slate-300 leading-relaxed mb-6">
                  Beyond corporate and enterprise technology, Brunet has spent decades as an <span className="text-white font-medium">independent entrepreneur</span>, building, operating, and sustaining businesses where outcomes—not ideas—determine survival.
                </p>
                <p className="text-slate-300 leading-relaxed mb-4">He understands firsthand that:</p>
                <ul className="space-y-3 mb-6">
                  {[
                    "Technology must earn its place in operations",
                    "Bad decisions cost real money",
                    "Complexity is the enemy of execution",
                    "Clarity is a competitive advantage"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-300">
                      <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-slate-300 leading-relaxed">
                  This lived reality informs the core philosophy behind A.I. Means Business: <span className="text-white font-medium">business owners do not need more tools—they need better decisions</span>.
                </p>
              </div>

              {/* Authorship */}
              <div className="glass p-8 rounded-2xl" data-testid="founder-authorship">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-white">
                    Author, Researcher, and Translator of Complexity
                  </h3>
                </div>
                <p className="text-slate-300 leading-relaxed mb-6">
                  R. Dale Brunet is the author of <span className="text-primary font-semibold">over 30 published books</span> examining artificial intelligence, automation, and technology's impact on business, labor, and society. His work focuses on translating complex technological shifts into <span className="text-white font-medium">plain-language insights</span> that business owners and leaders can actually use.
                </p>
                <p className="text-slate-300 leading-relaxed mb-4">Rather than promoting A.I. as a silver bullet, his writing consistently emphasizes:</p>
                <ul className="space-y-2">
                  {[
                    "Responsible adoption",
                    "Human governance",
                    "Long-term viability over short-term novelty",
                    "The hidden costs of uninformed action"
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-slate-300">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Why AI Means Business Exists */}
              <div data-testid="founder-why">
                <h3 className="font-heading text-2xl font-bold text-white mb-6">
                  Why A.I. Means Business Exists
                </h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  Brunet founded A.I. Means Business in response to a growing pattern he observed across industries:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {[
                    "Business owners sense that A.I. will affect their future",
                    "They are pressured to \"do something\" without understanding what",
                    "Vendors push tools before problems are clearly defined",
                    "Fear and hype replace thoughtful decision-making"
                  ].map((item, index) => (
                    <div key={index} className="p-4 rounded-lg bg-navy-950/50 border border-slate-800">
                      <p className="text-slate-300 text-sm">{item}</p>
                    </div>
                  ))}
                </div>
                <p className="text-lg text-white font-medium mb-4">
                  A.I. Means Business exists to interrupt that pattern.
                </p>
                <div className="space-y-2 text-slate-300">
                  <p>The firm does not sell software.</p>
                  <p>It does not implement tools by default.</p>
                  <p>It does not lock clients into dependency.</p>
                </div>
                <p className="text-slate-300 leading-relaxed mt-4">
                  Instead, it provides <span className="text-white font-medium">structured guidance</span>, informed by A.I. but governed by human judgment, designed to help leaders see clearly before they are forced to act.
                </p>
              </div>

              {/* Philosophy */}
              <div className="glass p-8 rounded-2xl border-primary/30" data-testid="founder-philosophy">
                <h3 className="font-heading text-2xl font-bold text-white mb-6">
                  A Measured, Human-First Approach to A.I.
                </h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  Brunet's philosophy is simple but uncommon:
                </p>
                <div className="space-y-4">
                  {[
                    "A.I. should support thinking, not replace it",
                    "A.I. should reduce uncertainty, not amplify it",
                    "A.I. should be invisible in outcomes, not performative"
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary font-bold text-sm">{index + 1}</span>
                      </div>
                      <p className="text-white font-medium">{item}</p>
                    </div>
                  ))}
                </div>
                <p className="text-slate-300 leading-relaxed mt-6">
                  At A.I. Means Business, artificial intelligence is used extensively behind the scenes to analyze, synthesize, and structure information—but <span className="text-primary font-semibold">never to make decisions on behalf of clients</span>.
                </p>
                <p className="text-lg text-white font-medium mt-4">
                  Human judgment remains central. Always.
                </p>
              </div>

              {/* Who This Work Is For */}
              <div data-testid="founder-audience">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-white">
                    Who This Work Is For
                  </h3>
                </div>
                <p className="text-slate-300 leading-relaxed mb-6">
                  R. Dale Brunet works primarily with:
                </p>
                <ul className="space-y-2 mb-8">
                  {[
                    "Established small and medium-sized businesses",
                    "Privately owned companies",
                    "Non-technical owners and leadership teams",
                    "Organizations seeking viability, not experimentation"
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-slate-300">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      {item}
                    </li>
                  ))}
                </ul>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-6 rounded-xl bg-red-950/20 border border-red-900/30">
                    <p className="text-red-400 font-medium mb-3">If you are looking for:</p>
                    <ul className="space-y-2 text-slate-400 text-sm">
                      <li>• A salesperson</li>
                      <li>• A tool evangelist</li>
                      <li>• A guaranteed outcome</li>
                    </ul>
                    <p className="text-slate-500 text-sm mt-3 italic">This is not the right fit.</p>
                  </div>
                  <div className="p-6 rounded-xl bg-green-950/20 border border-green-900/30">
                    <p className="text-green-400 font-medium mb-3">If you are looking for:</p>
                    <ul className="space-y-2 text-slate-300 text-sm">
                      <li>• Clear thinking</li>
                      <li>• Grounded guidance</li>
                      <li>• An experienced human guide through uncertainty</li>
                    </ul>
                    <p className="text-green-400 text-sm mt-3 font-medium">You are exactly who this work is for.</p>
                  </div>
                </div>
              </div>

              {/* Final Word */}
              <div className="border-t border-slate-800 pt-12" data-testid="founder-final">
                <h3 className="font-heading text-2xl font-bold text-white mb-6">
                  Final Word
                </h3>
                <p className="text-slate-300 leading-relaxed mb-4">
                  Technology changes quickly.
                </p>
                <p className="text-slate-300 leading-relaxed mb-4">
                  Businesses fail slowly—often because decisions are made too late or with incomplete understanding.
                </p>
                <p className="text-slate-300 leading-relaxed mb-4">
                  R. Dale Brunet's work is about <span className="text-white font-medium">creating space for better decisions before urgency removes choice</span>.
                </p>
                <p className="text-slate-300 leading-relaxed mb-4">
                  A.I. Means Business is not about keeping up with technology.
                </p>
                <p className="text-lg text-white font-medium">
                  It is about staying viable in a world where technology no longer waits.
                </p>
              </div>

              {/* CTA */}
              <div className="glass p-8 rounded-2xl border-primary/30" data-testid="founder-cta">
                <h3 className="font-heading text-xl font-bold text-white mb-4">
                  Next Step
                </h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  If you're ready to explore how A.I. affects <em>your</em> business—and what actually matters—you're invited to begin with a <span className="text-white font-medium">Free A.I. Business Evaluation</span>.
                </p>
                <p className="text-primary font-medium mb-6">Clarity comes first. No cost. No commitment.</p>
                <Link to="/get-started" data-testid="founder-cta-btn">
                  <Button className="bg-primary hover:bg-primary/90 text-white font-bold rounded-full px-8 py-4">
                    Get Your Free A.I. Business Evaluation
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default AboutFounderPage;
